import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';

import {
  ApiError,
  fetchMe,
  login as loginApi,
  logout as logoutApi,
  onUnauthorized,
  setAccessToken,
  type MeResponse,
} from '@/lib/api';

interface AuthContextValue {
  me: MeResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery<MeResponse | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await fetchMe();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });

  const clearMe = useCallback(() => {
    setAccessToken(null);
    queryClient.setQueryData<MeResponse | null>(['me'], null);
  }, [queryClient]);

  useEffect(() => {
    onUnauthorized(() => clearMe());
  }, [clearMe]);

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const tokens = await loginApi(email, password);
      setAccessToken(tokens.access_token);
      const me = await fetchMe();
      queryClient.setQueryData(['me'], me);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        await logoutApi();
      } finally {
        clearMe();
      }
    },
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      me: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: !!meQuery.data?.user,
      login: async (email, password) => {
        await loginMutation.mutateAsync({ email, password });
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
    }),
    [meQuery.data, meQuery.isLoading, loginMutation, logoutMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function primeInitialAuth(qc: QueryClient): void {
  // Dispara refresh no load inicial — se tiver cookie válido, popula me.
  qc.prefetchQuery({ queryKey: ['me'], queryFn: fetchMe }).catch(() => undefined);
}
